import { lazy, Suspense, type ReactNode } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { USE_FIXTURES } from '@/api/client'
import { useWhoAmI } from '@/api/queries'
import { LoginView } from '@/components/auth/LoginView'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Shell } from '@/components/shell/Shell'
import { Home } from '@/routes/Home'

// Per-route code-split (docs/design/00-design-system.md §7): only Home ships in
// the initial bundle; Fleet's TanStack Table/Virtual and the placeholder pages
// load on navigation.
const Fleet = lazy(() => import('@/routes/Fleet').then((m) => ({ default: m.Fleet })))
const ServiceDetail = lazy(() => import('@/routes/ServiceDetail').then((m) => ({ default: m.ServiceDetail })))
const MetricsPortal = lazy(() => import('@/routes/metrics/MetricsPortal').then((m) => ({ default: m.MetricsPortal })))
const Knowledge = lazy(() => import('@/routes/Knowledge').then((m) => ({ default: m.Knowledge })))
const SopLibrary = lazy(() => import('@/routes/knowledge/graph/SopLibrary').then((m) => ({ default: m.SopLibrary })))
const SopGraphPage = lazy(() => import('@/routes/knowledge/graph/SopGraphPage').then((m) => ({ default: m.SopGraphPage })))
const SopRunnerPage = lazy(() => import('@/routes/knowledge/graph/SopRunnerPage').then((m) => ({ default: m.SopRunnerPage })))
const NewSopPage = lazy(() => import('@/routes/knowledge/graph/NewSopPage').then((m) => ({ default: m.NewSopPage })))
const Alerts = lazy(() => import('@/routes/Alerts').then((m) => ({ default: m.Alerts })))
const Incidents = lazy(() => import('@/routes/Incidents').then((m) => ({ default: m.Incidents })))
const IncidentWarRoom = lazy(() => import('@/routes/incidents/IncidentWarRoom').then((m) => ({ default: m.IncidentWarRoom })))
const Customers = lazy(() => import('@/routes/Customers').then((m) => ({ default: m.Customers })))
const CustomerDetail = lazy(() => import('@/routes/customers/CustomerDetail').then((m) => ({ default: m.CustomerDetail })))
const Terminals = lazy(() => import('@/routes/Terminals').then((m) => ({ default: m.Terminals })))
const TerminalSessionView = lazy(() => import('@/routes/terminals/TerminalSessionView').then((m) => ({ default: m.TerminalSessionView })))
const Security = lazy(() => import('@/routes/Security').then((m) => ({ default: m.Security })))
const Leadership = lazy(() => import('@/routes/Leadership').then((m) => ({ default: m.Leadership })))
const Autopilot = lazy(() => import('@/routes/Autopilot').then((m) => ({ default: m.Autopilot })))
const Pricing = lazy(() => import('@/routes/Pricing').then((m) => ({ default: m.Pricing })))
const Infrastructure = lazy(() => import('@/routes/Infrastructure').then((m) => ({ default: m.Infrastructure })))
const InfrastructureCell = lazy(() => import('@/routes/InfrastructureCell').then((m) => ({ default: m.InfrastructureCell })))
const InfrastructureNode = lazy(() => import('@/routes/InfrastructureNode').then((m) => ({ default: m.InfrastructureNode })))
const OnboardNodeWizard = lazy(() =>
  import('@/routes/infrastructure/OnboardNodeWizard').then((m) => ({ default: m.OnboardNodeWizard })),
)
const DeboardNodeWizard = lazy(() =>
  import('@/routes/infrastructure/DeboardNodeWizard').then((m) => ({ default: m.DeboardNodeWizard })),
)
const Organization = lazy(() => import('@/routes/Organization').then((m) => ({ default: m.Organization })))
const Ai = lazy(() => import('@/routes/Ai').then((m) => ({ default: m.Ai })))
const ComingSoon = lazy(() => import('@/routes/ComingSoon').then((m) => ({ default: m.ComingSoon })))
// The per-service subportal ("Tracetown" world) — its own shell + nested pages,
// mounted OUTSIDE the main Shell so the service team gets a dedicated portal.
const SvcPortalShell = lazy(() => import('@/svcportal/SvcPortalShell').then((m) => ({ default: m.SvcPortalShell })))
const SvcToday = lazy(() => import('@/svcportal/pages/Today').then((m) => ({ default: m.Today })))
const SvcAlerts = lazy(() => import('@/svcportal/pages/Alerts').then((m) => ({ default: m.Alerts })))
const SvcGraphs = lazy(() => import('@/svcportal/pages/Graphs').then((m) => ({ default: m.Graphs })))
const SvcLogs = lazy(() => import('@/svcportal/pages/Logs').then((m) => ({ default: m.Logs })))
const SvcPromises = lazy(() => import('@/svcportal/pages/Promises').then((m) => ({ default: m.Promises })))
const SvcAct = lazy(() => import('@/svcportal/pages/Act').then((m) => ({ default: m.Act })))
const SvcHistory = lazy(() => import('@/svcportal/pages/History').then((m) => ({ default: m.History })))
const SvcSecurity = lazy(() => import('@/svcportal/pages/Security').then((m) => ({ default: m.Security })))
const SvcTeam = lazy(() => import('@/svcportal/pages/Team').then((m) => ({ default: m.Team })))
const SvcDeployments = lazy(() => import('@/svcportal/pages/Deployments').then((m) => ({ default: m.Deployments })))
const SvcDocs = lazy(() => import('@/svcportal/pages/Docs').then((m) => ({ default: m.Docs })))
const SvcAnnouncements = lazy(() => import('@/svcportal/pages/Announcements').then((m) => ({ default: m.Announcements })))
const SvcAutopilot = lazy(() => import('@/svcportal/pages/Autopilot').then((m) => ({ default: m.Autopilot })))
const SvcAsk = lazy(() => import('@/svcportal/pages/AskIam').then((m) => ({ default: m.AskIam })))
const SvcConfigure = lazy(() => import('@/svcportal/pages/Configure').then((m) => ({ default: m.Configure })))

const queryClient = new QueryClient()

/**
 * Gates every route behind a session (r0-authn): a 401 from GET
 * /auth/whoami — no session cookie, an expired one, or a rejected token —
 * shows the login view instead of the shell. This is the ONE place that
 * decision is made; individual routes/widgets never re-derive it.
 *
 * VITE_USE_FIXTURES=true (offline dev) has no live backend and thus no real
 * auth to gate — it renders the shell directly, same as every other
 * fixtures-only code path in this app.
 */
function AuthGate({ children }: { children: ReactNode }) {
  const { isLoading, isError } = useWhoAmI()
  if (USE_FIXTURES) return <>{children}</>
  if (isLoading) return <div className="p-6 text-[12.5px] text-ink-2">Loading…</div>
  if (isError) return <LoginView />
  return <>{children}</>
}

const COMING_SOON: Array<{ path: string; module: string }> = [
  { path: '/data', module: 'Data' },
  { path: '/admin', module: 'Admin' },
]

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={200}>
        <BrowserRouter>
          <AuthGate>
            <Suspense fallback={<div className="p-6 text-[12.5px] text-ink-2">Loading…</div>}>
              <Routes>
                <Route element={<Shell />}>
                  <Route path="/" element={<Home />} />
                  <Route path="/leadership" element={<Leadership />} />
                  <Route path="/autopilot" element={<Autopilot />} />
                  <Route path="/fleet" element={<Fleet />} />
                  <Route path="/fleet/:svc" element={<ServiceDetail />} />
                  {/* Metrics portal (portal 1 of 6). /observability keeps working
                      as a redirect to the new dedicated route. */}
                  <Route path="/metrics" element={<MetricsPortal />} />
                  <Route path="/metrics/:svc" element={<MetricsPortal />} />
                  <Route path="/observability" element={<Navigate to="/metrics" replace />} />
                  <Route path="/knowledge" element={<Knowledge />} />
                  {/* SOP fabric (Phase-1 B2): flowchart library, per-family
                      page (flowchart/runs/versions/authoring/analytics), the
                      new-SOP author, and THE runner. Static segments (new,
                      runs) outrank the dynamic :key. */}
                  <Route path="/knowledge/sops" element={<SopLibrary />} />
                  <Route path="/knowledge/sops/new" element={<NewSopPage />} />
                  <Route path="/knowledge/sops/runs/:id" element={<SopRunnerPage />} />
                  <Route path="/knowledge/sops/:key" element={<SopGraphPage />} />
                  <Route path="/alerts" element={<Alerts />} />
                  <Route path="/incidents" element={<Incidents />} />
                  <Route path="/incidents/:id" element={<IncidentWarRoom />} />
                  <Route path="/customers" element={<Customers />} />
                  <Route path="/customers/:id" element={<CustomerDetail />} />
                  <Route path="/terminals" element={<Terminals />} />
                  <Route path="/terminals/:id" element={<TerminalSessionView />} />
                  <Route path="/security" element={<Security />} />
                  {/* AI module (Phase-1): Studio agent-backends config now, a
                      CTO-agent chat surface in Phase 3 — sub-nav lives inside
                      the route so /ai stays the module root for both. */}
                  <Route path="/ai" element={<Ai />} />
                  <Route path="/teams" element={<Organization />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/infrastructure" element={<Infrastructure />} />
                  <Route path="/infrastructure/cells/:id" element={<InfrastructureCell />} />
                  <Route path="/infrastructure/cells/:id/onboard" element={<OnboardNodeWizard />} />
                  <Route path="/infrastructure/nodes/:id" element={<InfrastructureNode />} />
                  <Route path="/infrastructure/nodes/:id/deboard" element={<DeboardNodeWizard />} />
                  {COMING_SOON.map(({ path, module }) => (
                    <Route key={path} path={path} element={<ComingSoon module={module} awu="post-r0" />} />
                  ))}
                </Route>
                {/* Per-service subportal — a service team's dedicated world
                    (Founder-directed). OUTSIDE the main Shell: it brings its
                    own Tracetown-themed shell, nav and pages. iam first;
                    other services register in svcportal/registry.ts. */}
                <Route path="/svc/:svcKey" element={<SvcPortalShell />}>
                  <Route index element={<SvcToday />} />
                  <Route path="alerts" element={<SvcAlerts />} />
                  <Route path="graphs" element={<SvcGraphs />} />
                  <Route path="logs" element={<SvcLogs />} />
                  <Route path="promises" element={<SvcPromises />} />
                  <Route path="act" element={<SvcAct />} />
                  <Route path="history" element={<SvcHistory />} />
                  <Route path="security" element={<SvcSecurity />} />
                  <Route path="team" element={<SvcTeam />} />
                  <Route path="deployments" element={<SvcDeployments />} />
                  <Route path="docs" element={<SvcDocs />} />
                  <Route path="announcements" element={<SvcAnnouncements />} />
                  <Route path="autopilot" element={<SvcAutopilot />} />
                  <Route path="ask" element={<SvcAsk />} />
                  <Route path="configure" element={<SvcConfigure />} />
                </Route>
              </Routes>
            </Suspense>
          </AuthGate>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  )
}

export default App
